/**
 * Estilos do cadastro em etapas (out/2026): indicador de etapas, contadores de
 * UCs, forma de pagamento e caixa do Contrato de Adesão. Os cards de plano e da
 * estimativa mensal são os de /conta (`account.styles`) e o interruptor PF/PJ é
 * o de /atualizar-dados (`update-user.styles`) — mesma identidade, sem cópia.
 * Fontes e cores só pelos tokens do tema (`infra/theme`).
 */
import styled from 'styled-components';

export const Stepper = styled.ol`
  display: flex;
  justify-content: center;
  gap: 8px 24px;
  flex-wrap: wrap;
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
`;

export const StepItem = styled.li<{ active: boolean; done: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${(props) => props.theme.font.body.family};
  font-size: ${(props) => props.theme.font.body.size.small};
  font-weight: ${(props) =>
    props.active
      ? props.theme.font.body.weight.bold
      : props.theme.font.body.weight.regular};
  color: ${(props) =>
    props.active || props.done
      ? props.theme.color.text.tertiary
      : props.theme.color.text.disabled};

  span {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${(props) => props.theme.color.text.inverse};
    background-color: ${(props) =>
      props.active || props.done
        ? props.theme.color.ui.orange
        : props.theme.color.ui.shade3};
  }
`;

export const SectionTitle = styled.h4`
  margin: 24px 0 12px;
  font-family: ${(props) => props.theme.font.title.family};
  font-size: ${(props) => props.theme.font.title.size.h4};
  font-weight: ${(props) => props.theme.font.title.weight.semi};
  line-height: ${(props) => props.theme.font.title.lineHeight.h4};
  color: ${(props) => props.theme.color.text.tertiary};
`;

export const CounterRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const Counter = styled.div<{ disabled?: boolean }>`
  padding: 12px 14px;
  border: 1px solid ${(props) => props.theme.color.ui.shade2};
  border-radius: 3px;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  font-family: ${(props) => props.theme.font.body.family};

  > span {
    display: flex;
    align-items: center;
    font-size: ${(props) => props.theme.font.body.size.small};
    color: ${(props) => props.theme.color.text.primary};
  }
  > div {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
  }
  button {
    width: 32px;
    height: 32px;
    border-radius: 3px;
    border: 1px solid ${(props) => props.theme.color.ui.shade2};
    background: ${(props) => props.theme.color.bg.primary};
    font-size: ${(props) => props.theme.font.body.size.regular};
    font-weight: ${(props) => props.theme.font.body.weight.bold};
    line-height: 1;
    color: ${(props) => props.theme.color.text.tertiary};
    cursor: pointer;

    &:disabled {
      cursor: default;
    }
  }
  output {
    min-width: 32px;
    text-align: center;
    font-family: ${(props) => props.theme.font.title.family};
    font-size: ${(props) => props.theme.font.title.size.h3};
    font-weight: ${(props) => props.theme.font.title.weight.bold};
    color: ${(props) => props.theme.color.text.tertiary};
  }
`;

// Espaço do card de estimativa (o card em si é o de /conta)
export const EstimateWrapper = styled.div`
  margin-top: 16px;
`;

// Valor mensal resumido nas etapas 2 e 3
export const SummaryBar = styled.div`
  margin-top: 20px;
  padding: 12px 16px;
  border-radius: 3px;
  border-left: 4px solid ${(props) => props.theme.color.ui.orange};
  background: ${(props) => props.theme.color.bg.muted};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-family: ${(props) => props.theme.font.body.family};
  font-size: ${(props) => props.theme.font.body.size.small};
  color: ${(props) => props.theme.color.text.primary};

  > div {
    display: flex;
    flex-direction: column;
  }
  b {
    color: ${(props) => props.theme.color.text.tertiary};
  }
  strong {
    font-family: ${(props) => props.theme.font.title.family};
    font-size: ${(props) => props.theme.font.title.size.h4};
    font-weight: ${(props) => props.theme.font.title.weight.bold};
    color: ${(props) => props.theme.color.ui.orange};
    white-space: nowrap;
  }
`;

export const PaymentGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

export const PaymentOption = styled.button<{ selected?: boolean }>`
  flex: 1 1 160px;
  height: 44px;
  border-radius: 3px;
  background: ${(props) => props.theme.color.bg.primary};
  border: 2px solid
    ${(props) =>
      props.selected
        ? props.theme.color.ui.orange
        : props.theme.color.ui.shade2};
  font-family: ${(props) => props.theme.font.body.family};
  font-size: ${(props) => props.theme.font.body.size.small};
  font-weight: ${(props) => props.theme.font.body.weight.bold};
  color: ${(props) => props.theme.color.text.tertiary};
  cursor: pointer;

  small {
    font-weight: ${(props) => props.theme.font.body.weight.regular};
  }
  &:disabled {
    cursor: default;
    color: ${(props) => props.theme.color.text.disabled};
  }
`;

export const ContractBox = styled.div`
  border: 1px solid ${(props) => props.theme.color.ui.shade2};
  border-radius: 3px;
`;

export const ButtonsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

export const SecondaryButton = styled.button`
  width: 100%;
  margin-top: 20px;
  height: 38px;
  border-radius: 3px;
  border: 1px solid ${(props) => props.theme.color.ui.shade2};
  background: ${(props) => props.theme.color.bg.primary};
  color: ${(props) => props.theme.color.text.tertiary};
  font-family: ${(props) => props.theme.font.body.family};
  font-size: ${(props) => props.theme.font.body.size.regular};
  font-weight: ${(props) => props.theme.font.body.weight.bold};
  cursor: pointer;
`;

export const Hint = styled.p`
  margin: 6px 0 0;
  font-family: ${(props) => props.theme.font.body.family};
  font-size: ${(props) => props.theme.font.body.size.small};
  line-height: ${(props) => props.theme.font.body.lineHeight.small};
  color: ${(props) => props.theme.color.text.disabled};

  b {
    cursor: pointer;
    text-decoration: underline;
    font-weight: ${(props) => props.theme.font.body.weight.bold};
    color: ${(props) => props.theme.color.text.secondary};
  }
`;

export const FieldError = styled.p`
  margin: 6px 0 0;
  font-family: ${(props) => props.theme.font.body.family};
  font-size: ${(props) => props.theme.font.body.size.small};
  color: ${(props) => props.theme.color.text.danger};
`;
